/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { makeVar, ReactiveVar } from '@apollo/client';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */

export type ClipboardOperation = 'copy' | 'cut';

export interface ResourceClipboard {
  resourceIds: string[];
  operation: ClipboardOperation;
}

/* -------------------------------------------------------------------------- */
/*                                Reactive var                                */
/* -------------------------------------------------------------------------- */

/**
 * Reactive variable to store the clipboard data (resource IDs and operation type)
 * This allows sharing the clipboard state across components
 */
export const resourceClipboardVar: ReactiveVar<ResourceClipboard> =
  makeVar<ResourceClipboard>({
    resourceIds: [],
    operation: 'copy',
  });

/**
 * Helper function to set clipboard data with resource IDs and operation type
 */
export const setClipboardResources = (
  resourceIds: string[],
  operation: ClipboardOperation,
) => {
  resourceClipboardVar({ resourceIds, operation });
};

/**
 * Helper function to clear all clipboard data
 */
export const clearCopiedResourceIds = () => {
  resourceClipboardVar({ resourceIds: [], operation: 'copy' });
};

/**
 * Helper function to remove specific resource IDs from the clipboard
 */
export const removeCopiedResourceIds = (resourceIds: string[]) => {
  const clipboard = resourceClipboardVar();
  const filteredClipboardIds = clipboard.resourceIds.filter(
    (id) => !resourceIds.includes(id),
  );
  resourceClipboardVar({ ...clipboard, resourceIds: filteredClipboardIds });
};
